import {
  Checkbox as AntdCheckbox,
  Dropdown,
  InputNumber,
  Menu,
  Modal,
  notification,
  Slider,
} from 'antd';
import React, { useState } from 'react';
import {
  createUrl,
  Flex,
  FormGroup,
  historicalMessagesSubType,
  IntervalPreset,
  NetworkAsset,
  networkConfigurationSubType,
  openNotificationWithIcon,
  useAssetParams,
  useAssetQuery,
  useAssetTranslation,
} from '@mss/mss-component-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { intervalOffset } from '../../util/collectorHelpers';
import SubMenu from 'antd/lib/menu/SubMenu';
import Api from '../../services/Api';
import Button from '../../../common/Button';
import { useCollectorsQuery } from '../../services/queries';
import classNames from 'classnames';

export const MenuItem = ({
  className,
  ...props
}: React.ComponentProps<typeof Menu.Item>) => {
  return (
    <Menu.Item
      className={classNames(
        'py-2 px-6 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-gray-200',
        className
      )}
      {...props}
    />
  );
};

export const StyledSubMenu = ({
  className,
  ...props
}: React.ComponentProps<typeof SubMenu>) => {
  return (
    <SubMenu
      className={classNames(
        'py-1.5 px-3 hover:bg-[#f5f5f5] transition-colors ease duration-300 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-gray-200',
        className
      )}
      {...props}
    />
  );
};

type ActionMenuProps = {
  collector: any;
  update: any;
  collectorType: string;
  collectorsPreset?: any;
  canUpdate?: any;
  canView?: any;
};

export const formatExtraCategories = (
  category: string,
  collector_key: string
) => {
  if (category === 'running-config') return 'Running Configuration';
  if (category === 'ip route') return 'IP Route';
  if (collector_key === 'network_config')
    return networkConfigurationSubType(category);
  if (collector_key === 'historical_messages')
    return historicalMessagesSubType(category);
  return category;
};

export default function ActionMenu({
  collector,
  update,
  collectorType,
  collectorsPreset,
  canUpdate,
  canView,
}: ActionMenuProps) {
  const {
    collector_key,
    collector_type,
    enabled,
    id,
    interval,
    last_result_log,
    label,
  } = collector;
  const { assetType, assetSubType } = useAssetParams();
  const { t } = useAssetTranslation();
  const { data } = useAssetQuery<NetworkAsset>();
  const seriesValue = data?.series;
  const query = useCollectorsQuery({
    presetUrl: createUrl(
      { assetType, assetSubType },
      `collectors${seriesValue ? `/?series_id=${seriesValue}` : '/'}`,
      true
    ),
    disabled: !data,
  });

  const [isOpen, setOpen] = useState(false);
  const intervalSettings = collector.intervals;
  const hasInterval = intervalSettings.step !== 0 && id;
  const intervalUnit = intervalOffset(intervalSettings.step);
  const [newInterval, setInterval] = useState(
    interval || intervalSettings.default
  );

  const [logsModal, setLogsModal] = useState(false);

  const [extraOptions] = useState(
    collectorsPreset &&
      collector_type in collectorsPreset.data &&
      collectorsPreset.data[collector_type].extra_options
      ? collectorsPreset.data[collector_type].extra_options
      : []
  );
  const hasExtraOptions = ['network_config', 'historical_messages'].includes(
    collector.collector_key
  );
  const [extraOptionsDisabled, setExtraOptionsDisabled] = useState(false);

  const closeModal = () => setLogsModal(false);

  const intervalToMinutes = (value: number, unit: string) => {
    if (unit === 'hours') return value * 60;
    return value;
  };

  const minutesToInterval = (value: number, unit: string) => {
    if (unit === 'hours') return value / 60;
    return value;
  };

  const changeNewInterval = (value?: number) => (val: any) => {
    if (value || typeof val === 'number') {
      setInterval(value || intervalToMinutes(val, intervalUnit));
    }
  };
  const changeInterval = (value?: number) => (val: any) => {
    if (value || typeof val === 'number') {
      updateInterval(value || intervalToMinutes(val, intervalUnit));
    }
  };

  const changeExtraOption = async (e: any, option?: string) => {
    setExtraOptionsDisabled(true);
    e.stopPropagation();
    if (collector.extra_options?.filter_categories?.includes(option)) {
      collector.extra_options.filter_categories.splice(
        collector.extra_options.filter_categories.indexOf(option),
        1
      );
    } else {
      collector.extra_options.filter_categories = [
        ...(collector.extra_options.filter_categories || []),
        option,
      ];
    }
    await update({
      collector_type,
      collector_key,
      enabled,
      interval,
      extra_options: {
        filter_categories: collector.extra_options.filter_categories,
      },
    });
    setExtraOptionsDisabled(false);
  };

  const updateInterval = async (interval: string | number) => {
    await update({
      collector_type,
      collector_key,
      enabled,
      interval,
    });
    setInterval(interval);
  };

  const intervalProps = {
    value: minutesToInterval(newInterval, intervalUnit),
    min: minutesToInterval(intervalSettings.min, intervalUnit),
    max: minutesToInterval(intervalSettings.max, intervalUnit),
    step: minutesToInterval(intervalSettings.step, intervalUnit),
  };

  return (
    <>
      <Dropdown
        trigger={['click']}
        overlay={
          <Menu style={{ minWidth: 192 }}>
            <StyledSubMenu
              title={t('Set interval')}
              disabled={!hasInterval || !enabled || !canUpdate}
            >
              {intervalSettings.presets?.map((preset: IntervalPreset) => (
                <MenuItem
                  onClick={changeInterval(preset.value)}
                  key={preset.key}
                >
                  {preset.key}
                </MenuItem>
              ))}
              {hasInterval && (
                <MenuItem style={{ minWidth: 192 }} disabled>
                  <FormGroup
                    label={t(`Custom ({{unit}})`, {
                      unit: { hours: t('hours'), minutes: t('minutes') }[
                        intervalUnit
                      ],
                    })}
                    input={
                      <>
                        <InputNumber
                          {...intervalProps}
                          onChange={changeInterval()}
                          style={{ minWidth: '100%' }}
                        />
                        <Slider
                          {...intervalProps}
                          onChange={changeNewInterval()}
                          onAfterChange={changeInterval()}
                        />
                      </>
                    }
                    data-ta={'menu-SetInterval'}
                  />
                </MenuItem>
              )}
            </StyledSubMenu>
            {extraOptions?.length > 0 && hasExtraOptions && (
              <StyledSubMenu
                title={t('Data Selection')}
                disabled={!enabled || !canUpdate}
              >
                <Flex h="between" v="center" className="ml-12 mt-2 mb-2">
                  {t('Data Selection')}
                </Flex>
                {extraOptions.map((option: string) => (
                  <MenuItem key={option}>
                    <Flex h="between" v="center">
                      <AntdCheckbox
                        disabled={extraOptionsDisabled}
                        onClick={e => changeExtraOption(e, option)}
                        checked={collector.extra_options?.filter_categories?.includes(
                          option
                        )}
                      >
                        <span>
                          {t(
                            formatExtraCategories(
                              option,
                              collector.collector_key
                            )
                          )}
                        </span>
                      </AntdCheckbox>
                    </Flex>
                  </MenuItem>
                ))}
              </StyledSubMenu>
            )}
            <MenuItem
              disabled={!id || !enabled || !canUpdate}
              onClick={() => {
                Api.runCollector(id, (result: any) => {
                  if (result.status !== 'REJECTED') {
                    openNotificationWithIcon(
                      notification.success,
                      t('Task started'),
                      t(
                        'The task is started. Please wait, this might take a couple of minutes'
                      )
                    );
                    setTimeout(() => query.update(collector), 1000);
                  } else if (
                    collector.last_result_log?.includes('Heartbeat status down')
                  ) {
                    openNotificationWithIcon(
                      notification.error,
                      t('Task could not be started'),
                      t(
                        'Heartbeat status is down, the task could not be started.'
                      )
                    );
                  } else {
                    openNotificationWithIcon(
                      notification.error,
                      t('Task could not be started'),
                      t(
                        'Due to either technical issues or the task already running, the task could not be started.'
                      )
                    );
                  }
                });
              }}
              data-ta={'menu-RunTaskNow'}
            >
              {t('Run task now')}
            </MenuItem>
            <MenuItem
              disabled={!canView}
              onClick={() => {
                setLogsModal(true);
              }}
              data-ta={'menu-ShowLogs'}
            >
              {t('Show Logs')}
            </MenuItem>
          </Menu>
        }
      >
        <div
          style={{ width: 32, textAlign: 'center' }}
          onClick={() => setOpen(!isOpen)}
        >
          <FontAwesomeIcon icon={faEllipsisV} size="sm" />
        </div>
      </Dropdown>
      <Modal
        title={t(`(LABEL) (COLLECTORTYPE) last result log`, {
          Label: label,
          typeCollector: `(${collectorType})`,
          interpolation: { escapeValue: false },
        })}
        footer={[
          <Button ghost variant="primary" onClick={closeModal} key="close">
            {t('Close')}
          </Button>,
        ]}
        open={logsModal}
        closable={false}
      >
        <pre style={{ maxHeight: '65vh' }}>{last_result_log}</pre>
      </Modal>
    </>
  );
}
